import React, { useState, useRef, useEffect } from "react";
import Webcam from "react-webcam";
import { v4 as uuidv4 } from 'uuid';
import { Form, Button, Row, Col, Modal } from 'react-bootstrap';
import { API_BASE } from "../api";

function IntakeForm({ onSubmit }) {
  const [name, setName] = useState("");
  const [birthdate, setBirthdate] = useState('');
  const webcamRef = useRef(null);
  const [image, setImage] = useState(null);
  const [showIdModal, setShowIdModal] = useState(false);
  const [createdId, setCreatedId] = useState(null);

  // Camera selection state
  const [videoDevices, setVideoDevices] = useState([]);
  const [selectedDeviceId, setSelectedDeviceId] = useState('');

  useEffect(() => {
    let mounted = true;

    const getVideoDevices = async () => {
      if (!navigator.mediaDevices || !navigator.mediaDevices.enumerateDevices) {
        setVideoDevices([]);
        return;
      }

      try {
        // First enumerate devices. If labels are empty (no permission yet),
        // request a short permission to get device labels, then enumerate again.
        let devices = await navigator.mediaDevices.enumerateDevices();
        let videoInputs = devices.filter(d => d.kind === 'videoinput');

        const needsPermission = videoInputs.every(d => !d.label);
        if (needsPermission) {
          try {
            const stream = await navigator.mediaDevices.getUserMedia({ video: true });
            // stop tracks immediately
            stream.getTracks().forEach(t => t.stop());
            devices = await navigator.mediaDevices.enumerateDevices();
            videoInputs = devices.filter(d => d.kind === 'videoinput');
          } catch (err) {
            // permission denied or failed - proceed with whatever labels are available
            console.warn('Could not get camera permission to list labels:', err);
          }
        }

        if (!mounted) return;

        setVideoDevices(videoInputs);
        if (!selectedDeviceId && videoInputs.length > 0) {
          setSelectedDeviceId(videoInputs[0].deviceId);
        }
      } catch (err) {
        console.error('Error enumerating devices', err);
        setVideoDevices([]);
      }
    };

    getVideoDevices();

    return () => {
      mounted = false;
    };
  }, [selectedDeviceId]);

  const handleCapture = () => {
    if (!webcamRef.current) return;
    const shot = webcamRef.current.getScreenshot();
    setImage(shot);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const formData = new FormData();
      formData.append('naam', name);
      formData.append('geboortedatum', new Date(birthdate).toISOString().split('T')[0]);
      if (image) {
        const blob = await fetch(image).then(res => res.blob());
        const photoFilename = `${uuidv4()}.jpg`;
        formData.append('photo', blob, photoFilename);
        formData.append('profielfoto', photoFilename);
      }

      // Include selected camera id if you want to store which camera was used
      if (selectedDeviceId) {
        formData.append('cameraDeviceId', selectedDeviceId);
      }

      const response = await fetch(`${API_BASE}/deelnemers`, {
        method: 'POST',
        body: formData,
      });

      if (response.ok) {
        const data = await response.json();
        if (data && data.id !== undefined && data.id !== null) {
          setCreatedId(data.id);
          setShowIdModal(true);
        }
        setName("");
        setBirthdate("");
        setImage(null);
        if (onSubmit) onSubmit();
      } else {
        const errorData = await response.json();
        console.error("Error adding participant:", errorData.error);
      }
    } catch (error) {
      console.error("An error occurred:", error);
    }
  };

  const videoConstraints = {
    width: 640,
    height: 480,
    ...(selectedDeviceId ? { deviceId: { exact: selectedDeviceId } } : {})
  };

  return (
    <Form onSubmit={handleSubmit}>
      <Form.Group as={Row} controlId="formName">
        <Form.Label column sm="2">Name:</Form.Label>
        <Col sm="10">
          <Form.Control
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
          />
        </Col>
      </Form.Group>

      <Form.Group as={Row} controlId="formBirthdate" className="mt-3">
        <Form.Label column sm="2">Birthdate:</Form.Label>
        <Col sm="10">
          <Form.Control
            type="date"
            value={birthdate}
            onChange={(e) => setBirthdate(e.target.value)}
            required
          />
        </Col>
      </Form.Group>

      <Form.Group as={Row} className="mt-3">
        <Form.Label column sm="2">Camera:</Form.Label>
        <Col sm="10" className="d-flex align-items-center">
          <Form.Select
            value={selectedDeviceId}
            onChange={(e) => setSelectedDeviceId(e.target.value)}
            aria-label="Select camera"
          >
            {videoDevices.length === 0 && <option value="">No camera found</option>}
            {videoDevices.map((d) => (
              <option key={d.deviceId} value={d.deviceId}>
                {d.label || `Camera ${d.deviceId}`}
              </option>
            ))}
          </Form.Select>
          <Button
            variant="link"
            onClick={() => {
              // Re-scan devices (useful if user plugged in a camera after page load)
              if (navigator.mediaDevices && navigator.mediaDevices.enumerateDevices) {
                navigator.mediaDevices.enumerateDevices().then(devices => {
                  const videoInputs = devices.filter(d => d.kind === 'videoinput');
                  setVideoDevices(videoInputs);
                  if (!selectedDeviceId && videoInputs[0]) {
                    setSelectedDeviceId(videoInputs[0].deviceId);
                  }
                }).catch(err => console.error('Error re-enumerating devices', err));
              }
            }}
            style={{ marginLeft: '0.5rem' }}
          >
            Refresh
          </Button>
        </Col>
      </Form.Group>

      <Form.Group as={Row} className="mt-3">
        <Col sm="12" className="d-flex justify-content-center position-relative">
          {image ? (
            <img src={image} alt="Captured" style={{ width: '640px', height: '480px', objectFit: 'cover' }} />
          ) : (
            <Webcam
              audio={false}
              ref={webcamRef}
              screenshotFormat="image/jpeg"
              width={640}
              height={480}
              videoConstraints={videoConstraints}
            />
          )}
          <Button
            variant="secondary"
            onClick={handleCapture}
            style={{
              position: 'absolute',
              bottom: '10px',
              left: '50%',
              transform: 'translateX(-50%)'
            }}
          >
            Capture Photo
          </Button>
        </Col>
      </Form.Group>

      <Button
        variant="primary"
        type="submit"
        className="w-100 mt-3 mb-4"
        style={{ display: 'block', margin: '0 auto' }}
      >
        Submit
      </Button>

      <Modal show={showIdModal} onHide={() => setShowIdModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Participant created</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div style={{ fontSize: '4rem', fontWeight: 'bold', textAlign: 'center', letterSpacing: '2px' }}>
            {createdId !== null ? String(createdId).toUpperCase() : ''}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowIdModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </Form>
  );
}

export default IntakeForm;
